import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Form, Checkbox, Radio, message, Input } from 'antd';
import FormField, { FormFieldProps } from 'components/FormField';
import QuickRangePicker from 'components/QuickRangePicker';
import StoreSelector from 'components/StoreSelector';
import { MAX_SELECTED_LENGTH } from 'components/StoreSelector/hooks/useStoreSelector';
import { Formik, FormikConfig, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import dayjs from 'lib/dayjs';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { searchStoreList, StoreInfo } from 'services/api/store';
import { toUpperCase } from 'utils/toLowerCase';
import { quickDays } from '../hooks/useSearch';
import { toNumber, uniq } from 'lodash-es';

const StyledForm = styled(Form)`
  padding: 16px 16px 4px;
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item')} {
    margin-bottom: 12px;
    &-label {
      min-width: 72px;
    }
  }
`;

const StyledStoreSelector = styled(StoreSelector)`
  height: 30px;
  .store-selector-placeholder {
    padding: 0;
  }
`;

type FV = Review.Filte;

export type FilterFormHelpers = FormikHelpers<FV>;

export interface FilterFormProps extends FormikConfig<FV> {
  children?: (nodes: ReactNode, formProps: FormikProps<FV>) => ReactNode;
}

function FilterForm(props: FilterFormProps) {
  const { children, ...otherProps } = props;

  return (
    <Formik enableReinitialize {...otherProps}>
      {(formProps) => {
        const {
          values: { platform },
        } = formProps;

        const form = (
          <StyledForm>
            <Store />
            <ProductName />
            <TimeRange />
            {
              {
                shopee: <ShopeeStar />,
                lazada: <LazadaStar />,
                mercado: <ShopeeStar />,
                tiktok: <ShopeeStar />,
                tiktok_us: <ShopeeStar />,
              }[platform]
            }
            <Status />
            <HasContent />
          </StyledForm>
        );

        return children ? children(form, formProps) : form;
      }}
    </Formik>
  );
}

function Store() {
  const {
    values: { platform, region, follower, storeId },
    setValues,
  } = useFormikContext<FV>();

  const { t } = useTranslate();

  const [storeList, setStoreList] = useState<StoreInfo[] | undefined>();

  const [loading, setLoading] = useState(false);

  const refsCurrent = {
    t,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if ((!storeId || storeId.length === 0) && storeList && storeList.length) {
      setStoreList([]);

      return;
    }

    if ((storeList && storeList.length) || !(storeId && storeId.length)) {
      return;
    }

    setLoading(true);

    searchStoreList({
      storeIds: storeId.map((id) => parseInt(id, 10)),
      cursor: 0,
      page_size: MAX_SELECTED_LENGTH,
    })
      .then((res) => {
        setStoreList(res.data.data.data);

        setLoading(false);
      })
      .catch(() => {
        message.error(refs.current.t('review.获取店铺列表失败，请稍后再试'));

        setLoading(false);
      });
  }, [storeId, storeList]);

  return (
    <Form.Item label={t('review.店铺')}>
      {loading ? (
        <LoadingOutlined />
      ) : (
        <StyledStoreSelector
          scene="VIEW_REVIEW"
          platformLimit={toUpperCase(platform)}
          displayUnreadCount={false}
          displayTriggerIcon={false}
          defaultTriggerText={`+ ${t('review.选择店铺')}`}
          value={{
            region,
            follower,
            storeList,
          }}
          onChange={(value) => {
            const { region, follower, storeList } = value;

            setStoreList(storeList);

            setValues((values) => {
              return {
                ...values,
                region,
                follower,
                storeId: storeList ? storeList.map(({ storeId }) => storeId) : undefined,
              };
            });
          }}
        />
      )}
    </Form.Item>
  );
}

function TimeRange() {
  const {
    values: { reviewStartTime, reviewEndTime },
    setValues,
  } = useFormikContext<FV>();

  const { t } = useTranslate();

  return (
    <Form.Item label={t('review.评价时间')}>
      <QuickRangePicker
        style={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          lineHeight: '32px',
        }}
        value={
          reviewStartTime && reviewEndTime
            ? [dayjs(parseInt(reviewStartTime, 10)), dayjs(parseInt(reviewEndTime, 10))]
            : undefined
        }
        onChange={(range) => {
          const [start, end] = range || [];

          const reviewStartTime = start ? start.startOf('days').valueOf().toString() : undefined;
          const reviewEndTime = end ? end.endOf('days').valueOf().toString() : undefined;

          setValues((values) => {
            return {
              ...values,
              reviewStartTime,
              reviewEndTime,
            };
          });
        }}
        quickActions={quickDays.map((day) => {
          return {
            key: `${day}`,
            label: t('review.近x天', { x: day }),
            range: () => {
              return [
                dayjs()
                  .subtract(day - 1, 'day')
                  .startOf('day'),
                dayjs().endOf('day'),
              ];
            },
          };
        })}
        disabledDate={(date) => {
          if (date.isAfter(dayjs().endOf('days'))) {
            return true;
          }

          if (date.isBefore(dayjs().subtract(180, 'days').startOf('days'))) {
            return true;
          }

          return false;
        }}
      />
    </Form.Item>
  );
}

interface StatusOption {
  status: Review.StringStatus;
  label: ReactNode;
}

function Status() {
  const { t } = useTranslate();

  const statusOptions: StatusOption[] = [
    {
      status: '1',
      label: t('review.待处理'),
    },
    {
      status: '2',
      label: t('review.处理中'),
    },
    {
      status: '3',
      label: t('review.已处理'),
    },
    {
      status: '4',
      label: t('review.无需处理'),
    },
  ];

  return (
    <FormField<FV['handleStatus']> name="handleStatus" label={t('review.评价状态')}>
      {({ field: { value = [], onChange } }) => {
        return (
          <Checkbox.Group
            value={value}
            onChange={(value) => onChange(value.sort() as Review.StringStatus[])}
          >
            {statusOptions.map(({ status, label }) => {
              return (
                <Checkbox key={status} value={status}>
                  {label}
                </Checkbox>
              );
            })}
          </Checkbox.Group>
        );
      }}
    </FormField>
  );
}

function HasContent() {
  const { t } = useTranslate();

  return (
    <FormField<FV['hasContent']> name="hasContent" label={t('review.评价内容')}>
      {({ field: { value, onChange } }) => {
        return (
          <Radio.Group
            value={value}
            onChange={({ target: { value } }) => onChange(value as FV['hasContent'])}
          >
            <Radio value="all">{t('review.全部')}</Radio>
            <Radio value="1">{t('review.带文字/图像/视频')}</Radio>
            <Radio value="0">{t('review.无内容')}</Radio>
          </Radio.Group>
        );
      }}
    </FormField>
  );
}

function StarField(props: FormFieldProps<FV['reviewRating']>) {
  const { t } = useTranslate();

  return (
    <FormField<FV['reviewRating']> {...props}>
      {({ field: { value = [], onChange } }) => {
        const allStars = ['1', '2', '3', '4', '5'];

        return (
          <div>
            <Checkbox
              checked={value.length === allStars.length}
              indeterminate={value.length > 0 && value.length < allStars.length}
              onChange={({ target: { checked } }) => onChange(checked ? allStars : [])}
            >
              {t('feedback.全部')}
            </Checkbox>
            {allStars.map((star) => {
              return (
                <Checkbox
                  key={star}
                  checked={value.includes(star)}
                  onChange={({ target: { checked } }) => {
                    onChange(
                      checked ? uniq([...value, star]).sort() : value.filter((_) => _ !== star),
                    );
                  }}
                >
                  {t('review.x星', { count: toNumber(star) })}
                </Checkbox>
              );
            })}
          </div>
        );
      }}
    </FormField>
  );
}

function ShopeeStar() {
  const { t } = useTranslate();

  return <StarField name="reviewRating" label={t('review.评价评分')} />;
}

function LazadaStar() {
  const { t } = useTranslate();

  return (
    <>
      <StarField name="reviewRating" label={t('review.整体评分')} />
      <StarField name="productRating" label={t('review.商品质量')} />
      <StarField name="logisticsRating" label={t('review.物流评分')} />
      <StarField name="sellerRating" label={t('review.卖家服务评分')} />
    </>
  );
}

function ProductName() {
  const { t } = useTranslate();

  return (
    <FormField<FV['spuName']> name="spuName" label={t('review.商品')}>
      {({ field: { value, onChange } }) => {
        return (
          <Input
            placeholder={t('sider.商品名称')}
            value={value}
            onChange={(value) => onChange(value)}
            allowClear
          />
        );
      }}
    </FormField>
  );
}

export default FilterForm;
